/**
 * Modal zum Anlegen/Bearbeiten eines Kalender-Eintrags (Notiz / Frei / Urlaub /
 * Krank / Wunschfrei) — offline-fähig über data/calendarEntries.
 *
 * Notizen sind reine Vermerke (sofort „gültig"). Abwesenheiten starten als
 * „ausstehend" und können vom Chef/Manager freigegeben werden.
 */

import { useState, type FormEvent } from 'react';
import { useCreateCalendarEntry, useUpdateCalendarEntry } from '../data/calendarEntries';
import type { LocalCalendarEntry } from '../db/schema';
import { ENTRY_TYPE_META, needsApproval, type CalendarEntryType } from '../lib/calendarEntries';
import { hapticError, hapticSuccess } from '../lib/haptics';
import { formatDate } from '../lib/format';

const TYPES: CalendarEntryType[] = ['off', 'vacation', 'sick', 'wish_off', 'note'];
// Gesamtprüfung 11.09.2026: lokales Datum statt UTC (sonst nach Mitternacht bis 01/02 Uhr noch „gestern“).
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

interface Props {
  entry?: LocalCalendarEntry;
  defaultDate?: string;
  onClose: () => void;
  onSaved: (e: LocalCalendarEntry) => void;
}

export function EntryFormModal({ entry, defaultDate, onClose, onSaved }: Props) {
  const create = useCreateCalendarEntry();
  const update = useUpdateCalendarEntry();

  const [type, setType] = useState<CalendarEntryType>(entry?.type ?? 'off');
  const [date, setDate] = useState(entry?.date ?? defaultDate ?? todayISO());
  const [title, setTitle] = useState(entry?.title ?? '');
  const [note, setNote] = useState(entry?.note ?? '');
  const [halfDay, setHalfDay] = useState(entry?.halfDay ?? false);
  const [error, setError] = useState<string | null>(null);

  const busy = create.isPending || update.isPending;
  const isNote = type === 'note';

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const input = {
      type,
      date,
      title: title.trim() ? title.trim() : null,
      note: note.trim() ? note.trim() : null,
      halfDay: isNote ? false : halfDay,
    };
    if (isNote && !input.title && !input.note) {
      setError('Bitte einen Text für die Notiz eingeben.');
      hapticError();
      return;
    }
    try {
      const saved = entry
        ? await update.mutateAsync({ id: entry.id, input })
        : await create.mutateAsync(input);
      hapticSuccess();
      onSaved(saved);
    } catch (err) {
      hapticError();
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-surface-2 rounded-t-2xl sm:rounded-lg p-4 sm:p-5 w-full max-w-md max-h-[92vh] sm:max-h-[90vh] overflow-y-auto border border-border/60">
        <h3 className="text-lg font-semibold mb-4">
          {entry ? 'Eintrag bearbeiten' : 'Notiz / Frei / Urlaub'}
        </h3>
        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <span className="block text-sm text-slate-300 mb-1.5">Art</span>
            <div className="flex flex-wrap gap-2">
              {TYPES.map((t) => {
                const meta = ENTRY_TYPE_META[t];
                const active = type === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setType(t)}
                    className="rounded-full px-3 py-1.5 text-sm !min-h-0 transition border"
                    style={
                      active
                        ? { backgroundColor: meta.color, borderColor: meta.color, color: '#fff' }
                        : { backgroundColor: '#273348', borderColor: '#3a4763', color: '#cbd5e1' }
                    }
                  >
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>

          <label className="block">
            <span className="block text-sm text-slate-300 mb-1">Datum</span>
            <input
              required
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="input"
            />
          </label>

          <label className="block">
            <span className="block text-sm text-slate-300 mb-1">
              {isNote ? 'Notiz' : 'Titel (optional)'}
            </span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="input"
              placeholder={isNote ? 'z. B. Arzttermin 10:00' : ENTRY_TYPE_META[type].label}
              autoFocus={isNote}
            />
          </label>

          {!isNote && (
            <>
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={halfDay}
                  onChange={(e) => setHalfDay(e.target.checked)}
                  className="w-4 h-4 accent-brand"
                />
                Halber Tag
              </label>
              <label className="block">
                <span className="block text-sm text-slate-300 mb-1">Grund / Anmerkung (optional)</span>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  className="input resize-y"
                  placeholder="Optional"
                />
              </label>
              {needsApproval(type) && (
                <p className="text-[11px] text-slate-500">
                  {entry?.status === 'approved'
                    ? '✓ Vom Chef freigegeben.'
                    : entry?.status === 'rejected'
                      ? '✗ Vom Chef abgelehnt.'
                      : 'Wird dem Chef/der Filialleitung zur Freigabe angezeigt.'}
                </p>
              )}
            </>
          )}

          {error && <p className="text-sm text-red-400">{error}</p>}

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost w-full sm:w-auto">
              Abbrechen
            </button>
            <button type="submit" disabled={busy} className="btn w-full sm:w-auto">
              {busy ? 'Speichere…' : entry ? 'Speichern' : `${ENTRY_TYPE_META[type].label} eintragen`}
            </button>
          </div>
        </form>
        <p className="sr-only">{formatDate(date)}</p>
      </div>
    </div>
  );
}
